import { useState } from 'react';
import { FilterCatalog } from '../../core/model/filterCatalog';
import { EMPTY_FILTERS, activeFilterCount, type SearchFilters } from '../../core/model/search';
import { Formatters } from '../../core/util/formatters';
import { S } from '../../i18n/strings';
import { Dialog } from './Dialog';

/**
 * Hoja de filtros.
 *
 * Trabaja sobre una copia local y solo publica los cambios al pulsar "Ver resultados": tocar
 * cinco filtros no debe disparar cinco búsquedas ni dejar la lista parpadeando bajo el dedo.
 */
export function FiltersSheet({
  initial,
  resultCount,
  onApply,
  onClose,
}: {
  initial: SearchFilters;
  resultCount: number | null;
  onApply: (filters: SearchFilters) => void;
  onClose: () => void;
}) {
  const [draft, setDraft] = useState<SearchFilters>(initial);

  const toggle = <T,>(list: readonly T[], value: T): T[] =>
    list.includes(value) ? list.filter((v) => v !== value) : [...list, value];

  return (
    <Dialog
      title={S.searchFilters}
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            className="btn btn--text"
            onClick={() => setDraft(EMPTY_FILTERS)}
            disabled={activeFilterCount(draft) === 0}
          >
            {S.searchClearFilters}
          </button>
          <button
            type="button"
            className="btn"
            onClick={() => {
              onApply(draft);
              onClose();
            }}
          >
            {resultCount !== null ? `${S.filtersApply} (${resultCount})` : S.filtersApply}
          </button>
        </>
      }
    >
      <section className="section">
        <h3>{S.filtersCuisine}</h3>
        <div className="row">
          {FilterCatalog.cuisines.map((cuisine) => (
            <button
              key={cuisine}
              type="button"
              className="chip"
              aria-pressed={draft.cuisines.includes(cuisine)}
              onClick={() => setDraft({ ...draft, cuisines: toggle(draft.cuisines, cuisine) })}
            >
              {cuisine}
            </button>
          ))}
        </div>
      </section>

      <section className="section">
        <h3>{S.filtersPrice}</h3>
        <div className="row">
          {[1, 2, 3, 4].map((level) => (
            <button
              key={level}
              type="button"
              className="chip"
              aria-pressed={draft.priceLevels.includes(level)}
              onClick={() => setDraft({ ...draft, priceLevels: toggle(draft.priceLevels, level) })}
            >
              {Formatters.priceLevel(level)}
            </button>
          ))}
        </div>
      </section>

      <section className="section">
        <h3>{S.filtersRating}</h3>
        <div className="row">
          {[null, 3.5, 4, 4.5].map((rating) => (
            <button
              key={String(rating)}
              type="button"
              className="chip"
              aria-pressed={draft.minRating === rating}
              onClick={() => setDraft({ ...draft, minRating: rating })}
            >
              {rating === null ? S.filtersAny : `${rating.toFixed(1)}+`}
            </button>
          ))}
        </div>
      </section>

      <section className="section">
        <h3>{S.filtersDistance}</h3>
        <div className="row">
          {[null, 1000, 3000, 10000].map((meters) => (
            <button
              key={String(meters)}
              type="button"
              className="chip"
              aria-pressed={draft.maxDistanceMeters === meters}
              onClick={() => setDraft({ ...draft, maxDistanceMeters: meters })}
            >
              {meters === null ? S.filtersAny : Formatters.distance(meters)}
            </button>
          ))}
        </div>
      </section>

      <section className="section">
        <h3>{S.filtersAmenities}</h3>
        <div className="row">
          {FilterCatalog.amenities.map((amenity) => (
            <button
              key={amenity}
              type="button"
              className="chip"
              aria-pressed={draft.amenities.includes(amenity)}
              onClick={() => setDraft({ ...draft, amenities: toggle(draft.amenities, amenity) })}
            >
              {amenity}
            </button>
          ))}
        </div>
      </section>

      <label className="checkbox">
        <input
          type="checkbox"
          checked={draft.onlyWithAvailability}
          onChange={(event) => setDraft({ ...draft, onlyWithAvailability: event.target.checked })}
        />
        <span>{S.filtersOnlyAvailable}</span>
      </label>
    </Dialog>
  );
}
